import { Alert, Card, CircularProgress, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ChartPoint } from '../../api/queries';
import {
  useAdminSummary,
  useCoverage,
  useSpendByCategory,
  useSpendSummary,
  useTopSpend,
  useUserDistribution,
} from '../../api/queries';
import { usd } from '../../lib/format';

// Distinct hues anchored on the Hut 8 greens, spread enough that adjacent
// donut slices stay tellable-apart.
const COLORS = [
  '#B4FE00',
  '#3D7A00',
  '#8FCB3A',
  '#5C8001',
  '#D4E09B',
  '#2F5D14',
  '#A3C14A',
  '#6FA22E',
  '#445E20',
];

const TOOLTIP_STYLE = {
  background: '#161616',
  border: '1px solid #333',
  borderRadius: 8,
  color: '#fafafa',
} as const;
// recharts colors item/label text separately from the container — without
// these the values render dark-on-dark.
const TOOLTIP_ITEM_STYLE = { color: '#fafafa' } as const;
const TOOLTIP_LABEL_STYLE = { color: '#999' } as const;

const truncate = (s: string, n = 18) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** Collapse a long tail into a single "Other" slice. */
function topN(points: ChartPoint[], n: number): ChartPoint[] {
  if (points.length <= n + 1) return points;
  const head = points.slice(0, n);
  const rest = points.slice(n).reduce((s, p) => s + p.value, 0);
  return [...head, { label: 'Other', value: rest }];
}

export default function AdminSummary() {
  const summary = useAdminSummary();
  const spend = useSpendSummary();
  const topSpend = useTopSpend();
  const byCategory = useSpendByCategory();
  const userDist = useUserDistribution();
  const coverage = useCoverage();

  const categoryData = useMemo(() => topN(byCategory.data ?? [], 8), [byCategory.data]);

  if (summary.isLoading) return <CircularProgress />;
  if (summary.error) return <Alert severity="error">Failed: {String(summary.error)}</Alert>;

  const s = summary.data!;

  return (
    <Stack spacing={3}>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi label="Apps" value={String(s.apps_count)} />
        <Kpi label="People with access" value={String(s.accounts_count)} />
        <Kpi label="Matrix total" value={usd(s.total_matrix)} />
        <Kpi label="Actual total" value={usd(s.total_actual)} />
        <Kpi label="Spend YTD" value={usd(spend.data?.ytd)} />
        <Kpi label="Open requests" value={String(s.open_requests)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top spend (YTD)">
          <ResponsiveContainer width="100%" height={340}>
            <BarChart
              data={topSpend.data ?? []}
              layout="vertical"
              margin={{ left: 8, right: 24, top: 4, bottom: 4 }}
            >
              <CartesianGrid horizontal={false} stroke="#262626" />
              <XAxis
                type="number"
                tickFormatter={(v) => usd(Number(v))}
                stroke="#777"
                fontSize={12}
              />
              <YAxis
                type="category"
                dataKey="label"
                width={150}
                stroke="#777"
                fontSize={12}
                tickFormatter={(v) => truncate(String(v), 20)}
                interval={0}
              />
              <Tooltip
                formatter={(v) => [usd(Number(v)), 'YTD']}
                contentStyle={TOOLTIP_STYLE}
                itemStyle={TOOLTIP_ITEM_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              />
              <Bar dataKey="value" fill={COLORS[0]} radius={[0, 4, 4, 0]} barSize={18} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Spend by category (YTD)" subtitle="Top 8 — the rest grouped as Other">
          <ResponsiveContainer width="100%" height={340}>
            <PieChart>
              <Pie
                data={categoryData}
                dataKey="value"
                nameKey="label"
                innerRadius={75}
                outerRadius={115}
                paddingAngle={2}
                strokeWidth={0}
              >
                {categoryData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v) => usd(Number(v))}
                contentStyle={TOOLTIP_STYLE}
                itemStyle={TOOLTIP_ITEM_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
              />
              <Legend
                layout="vertical"
                align="right"
                verticalAlign="middle"
                iconType="circle"
                iconSize={9}
                formatter={(v: string) => (
                  <span style={{ color: '#bbb', fontSize: 12 }}>{truncate(v, 24)}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Apps by user count">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={userDist.data ?? []} margin={{ top: 8 }}>
              <CartesianGrid vertical={false} stroke="#262626" />
              <XAxis dataKey="label" stroke="#777" fontSize={12} />
              <YAxis allowDecimals={false} stroke="#777" fontSize={12} />
              <Tooltip
                formatter={(v) => [v, 'apps']}
                contentStyle={TOOLTIP_STYLE}
                itemStyle={TOOLTIP_ITEM_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              />
              <Bar dataKey="value" fill={COLORS[2]} radius={[4, 4, 0, 0]} barSize={42} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Signal coverage" subtitle="Apps seen via SSO assignments vs spend feeds">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={coverage.data ?? []}
                dataKey="value"
                nameKey="label"
                innerRadius={62}
                outerRadius={98}
                paddingAngle={2}
                strokeWidth={0}
              >
                {(coverage.data ?? []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v) => [v, 'apps']}
                contentStyle={TOOLTIP_STYLE}
                itemStyle={TOOLTIP_ITEM_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
              />
              <Legend
                iconType="circle"
                iconSize={9}
                formatter={(v: string) => <span style={{ color: '#bbb', fontSize: 12 }}>{v}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </Stack>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card variant="outlined" className="p-4">
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </Card>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Card variant="outlined" className="p-4">
      <Typography variant="subtitle2">{title}</Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} gutterBottom>
          {subtitle}
        </Typography>
      )}
      {children}
    </Card>
  );
}
